import { useMutation } from '@apollo/client/react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@optools/components/ui/alert-dialog';
import { Button } from '@optools/components/ui/button';
import { Card } from '@optools/components/ui/card';
import { DELETE_BROADCAST_SOURCE_OVERRIDE } from '@optools/services/operator-gateway/btc/mutations';
import {
  BroadcastSourceOverride,
  BroadcastSourceOverridesQuery,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { Trash } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { formatProgramSourceValue } from '../utils/broadcast-source-overrides';

export interface DeleteBroadcastSourceOverrideProps {
  broadcastSourceOverride: BroadcastSourceOverride;
}

export function DeleteBroadcastSourceOverride({
  broadcastSourceOverride,
}: DeleteBroadcastSourceOverrideProps) {
  const [open, setIsOpen] = useState(false);

  const { callSign, gameSource, id, postgameSource, pregameSource } = broadcastSourceOverride;

  const [deleteBroadcastSourceOverride, { loading }] = useMutation(
    DELETE_BROADCAST_SOURCE_OVERRIDE,
    {
      onCompleted: (data) => {
        const title = 'Delete Broadcast Source Override';
        if (!data?.deleteBroadcastSourceOverride) {
          toast.error(title, {
            description: `Failed to delete broadcast source override with id: ${id}`,
            duration: Infinity,
          });
          return;
        }

        toast.success(title, {
          description: `Successfully deleted broadcast source override with id: ${id}`,
        });
        setIsOpen(false);
      },
      update: (cache, { data }) => {
        if (!data?.deleteBroadcastSourceOverride) return;

        cache.modify<BroadcastSourceOverridesQuery>({
          fields: {
            broadcastSourceOverrides(existing, { readField }) {
              return existing.filter((item) => id !== readField('id', item));
            },
          },
        });
      },
    },
  );

  const onDelete = () => deleteBroadcastSourceOverride({ variables: { id: parseInt(id) } });

  return (
    <AlertDialog open={open} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <Button aria-label="delete broadcast source override" size="icon" variant="destructive">
          <Trash />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogTitle>Delete Broadcast Source Override</AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you wish to delete the following broadcast source override?
        </AlertDialogDescription>
        <Card className="flex flex-col flex-wrap gap-2 p-2 text-sm">
          <div className="flex gap-3">
            <dl className="flex gap-1">
              <dt className="font-semibold">ID:</dt>
              <dd>{id}</dd>
            </dl>
            <dl className="flex gap-1">
              <dt className="font-semibold">Call Sign:</dt>
              <dd>{callSign}</dd>
            </dl>
          </div>
          <dl className="flex gap-1">
            <dt className="font-semibold">Pre-Game Source:</dt>
            <dd className="capitalize">{formatProgramSourceValue(pregameSource)}</dd>
          </dl>
          <dl className="flex gap-1">
            <dt className="font-semibold">Game Source:</dt>
            <dd className="capitalize">{formatProgramSourceValue(gameSource)}</dd>
          </dl>
          <dl className="flex gap-1">
            <dt className="font-semibold">Post-Game Source:</dt>
            <dd className="capitalize">{formatProgramSourceValue(postgameSource)}</dd>
          </dl>
        </Card>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <Button disabled={loading} loading={loading} variant="destructive" onClick={onDelete}>
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
